import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Starfield from "@/components/Starfield";
import PaymentBadges from "@/components/PaymentBadges";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/tools", label: "AI Tools" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function MarketingLayout({ children }) {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [site, setSite] = React.useState(null);
  const loc = useLocation();
  React.useEffect(() => setOpen(false), [loc.pathname]);
  React.useEffect(() => { api.get("/settings/site").then((r) => setSite(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen flex flex-col relative">
      <Starfield />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#05050A]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
            <span className="bx-glow-icon" style={{ width: 36, height: 36, borderRadius: 10 }}>
              <Sparkles size={18} />
            </span>
            <div className="leading-tight">
              <div className="bx-heading font-extrabold text-lg tracking-tight">BINAREX<span className="text-purple-400">.CO</span></div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400">AI Powered Solutions</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  loc.pathname === n.to ? "text-white bg-white/5" : "text-slate-300 hover:text-white hover:bg-white/5"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"} data-testid="header-dashboard-btn" className="bx-btn-primary text-sm">
                {user.role === "admin" ? "Admin Panel" : "Dashboard"}
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="header-login-btn" className="bx-btn-ghost text-sm">Login</Link>
                <Link to="/register" data-testid="header-register-btn" className="bx-btn-primary text-sm">Sign Up</Link>
              </>
            )}
          </div>

          <button data-testid="mobile-menu-toggle" className="md:hidden text-slate-200" onClick={() => setOpen((v) => !v)} aria-label="Open menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 bg-[#0B0F19]">
            <div className="px-5 py-4 flex flex-col gap-2">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className="py-2 text-slate-200">
                  {n.label}
                </Link>
              ))}
              <div className="bx-divider my-2" />
              {user ? (
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="bx-btn-primary">Open Dashboard</Link>
              ) : (
                <div className="flex gap-2">
                  <Link to="/login" className="bx-btn-ghost flex-1 justify-center">Login</Link>
                  <Link to="/register" className="bx-btn-primary flex-1 justify-center">Sign Up</Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 relative z-10">{children}</main>

      <footer className="border-t border-white/10 bg-[#05050A]/60">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <span className="bx-glow-icon" style={{ width: 32, height: 32, borderRadius: 8 }}><Sparkles size={16} /></span>
              <div className="bx-heading font-extrabold text-lg">BINAREX<span className="text-purple-400">.CO</span></div>
            </div>
            <p className="text-sm text-slate-400 max-w-xs">All-in-one AI tools platform by BINAREX AND CO. One account, one credit balance, 25+ tools.</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Platform</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/tools" className="text-slate-300 hover:text-white">AI Tools</Link></li>
              <li><Link to="/pricing" className="text-slate-300 hover:text-white">Pricing</Link></li>
              <li><Link to="/buy-credits" className="text-slate-300 hover:text-white">Buy Credits</Link></li>
              <li><Link to="/blog" className="text-slate-300 hover:text-white">Blog</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Company</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-slate-300 hover:text-white">About</Link></li>
              <li><Link to="/contact" className="text-slate-300 hover:text-white">Contact</Link></li>
              <li><Link to="/faq" className="text-slate-300 hover:text-white">FAQ</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Legal</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy" className="text-slate-300 hover:text-white">Privacy</Link></li>
              <li><Link to="/terms" className="text-slate-300 hover:text-white">Terms</Link></li>
              <li><Link to="/refund" className="text-slate-300 hover:text-white">Refund</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col gap-3">
            {site?.footer_show_accepted_payments && (
              <PaymentBadges
                brands={site.footer_accepted_brands || []}
                processorText={site.footer_payment_processor_text || ""}
                logos={site.footer_brand_logos || {}}
              />
            )}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-slate-500">© {new Date().getFullYear()} BINAREX AND CO. All rights reserved.</p>
              <p className="text-xs text-slate-500">Built with care · GPT + Gemini Nano Banana</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
